import React from "react";
import SidebarDraggableField from "./SidebarDraggableField";
import { FieldData } from "./DraggableResizableField";

export interface SignerOption {
  id: string;
  name: string;
  /** Tailwind bg color class (e.g. "bg-blue-500"). Used for the chip. */
  color?: string;
  /** Initials painted before the date on the signed PDF. Editable here. */
  initials?: string;
}

const defaultFieldTypes = ["Signature", "Tampon", "Text", "Date"];

interface SignatureSidebarProps {
  editorMode: string;
  handleEditorMode: (mode: string) => void;
  handleDownload?: () => void;
  onSign: () => void;
  handleRemoveTextField?: (index: number) => void;
  setIsModalOpen?: (open: boolean) => void;
  onEdit: () => void;
  customTextFields?: string[];
  loadingData: boolean;
  onClose: () => void;
  fields: FieldData[];
  onlyEdit?: boolean;
  signButtonLabel?: string;
  editButtonLabel?: string;
  /**
   * Inline mode: when a non-empty signers array is supplied, the sidebar
   * switches to the streamlined "Bon pour accord" flow — one draggable
   * bundle per signer, no Text / Date / Tampon extras, and a coverage
   * summary showing field counts per signer.
   */
  signers?: SignerOption[];
  activeSignerId?: string;
  onActiveSignerChange?: (id: string) => void;
  onFieldDelete?: (id: number) => void;
  /** Edit a signer's initials inline from this sidebar (inline mode only). */
  onSignerInitialsChange?: (id: string, initials: string) => void;
}

function SignatureSidebar({
  editorMode,
  handleEditorMode,
  handleDownload,
  onSign,
  handleRemoveTextField,
  setIsModalOpen,
  onEdit,
  customTextFields = [],
  loadingData,
  onClose,
  fields,
  onlyEdit = false,
  signButtonLabel = "Prêt pour signature",
  editButtonLabel = "Enregistrer les modifications",
  signers,
  activeSignerId,
  onActiveSignerChange,
  onFieldDelete,
  onSignerInitialsChange,
}: SignatureSidebarProps) {
  const inlineMode = !!signers && signers.length > 0;

  const handlePrepareSignature = () => {
    handleEditorMode("sign");
  };

  const handleEditPDF = () => {
    handleEditorMode("edit");
  };

  const activeSigner = inlineMode
    ? signers!.find((s) => s.id === activeSignerId) || signers![0]
    : null;

  return (
    <div className="w-96 min-w-96 p-5 h-full overflow-auto bg-white border-r border-neutral-200">
      {editorMode === "choice" && (
        <div className="flex flex-col gap-3">
          <button
            onClick={handlePrepareSignature}
            className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-neutral-800 text-base"
          >
            Préparer une signature
          </button>
          <button
            onClick={handleEditPDF}
            className="w-full px-6 py-3 bg-neutral-100 text-black rounded-lg hover:bg-neutral-200 text-base"
          >
            Modifier le PDF
          </button>
          {handleDownload && (
            <button
              onClick={handleDownload}
              className="w-full px-6 py-3 bg-neutral-100 text-black rounded-lg hover:bg-neutral-200 text-base"
            >
              Télécharger
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-neutral-100 text-black rounded-lg hover:bg-neutral-200 text-base"
          >
            Fermer
          </button>
        </div>
      )}

      {editorMode === "sign" && inlineMode && (
        <div className="flex flex-col gap-4 h-full">
          <div>
            <div className="text-h2 font-semibold text-neutral-900">
              Placer les zones de signature
            </div>
            <div className="text-h4 text-neutral-500 mt-1">
              Sélectionnez un signataire puis glissez la zone « Bon pour accord »
              sur le document, autant de fois que nécessaire.
            </div>
          </div>

          {/* Signer chips */}
          <div className="flex flex-col gap-2">
            <div className="text-h4 font-medium text-neutral-600">Signataire</div>
            <div className="flex flex-row flex-wrap gap-1">
              {signers!.map((s) => {
                const count = fields.filter((f) => f.signer_id === s.id).length;
                const selected = s.id === activeSigner?.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => onActiveSignerChange?.(s.id)}
                    className={`flex flex-row items-center gap-2 px-3 py-1.5 rounded-full border text-h4 transition-colors ${
                      selected
                        ? "bg-neutral-900 text-white border-neutral-900"
                        : "bg-white text-neutral-700 border-neutral-300 hover:border-neutral-500"
                    }`}
                  >
                    <span
                      className={`w-2 h-2 rounded-full ${
                        s.color || "bg-neutral-400"
                      }`}
                    />
                    <span className="max-w-[140px] truncate">{s.name}</span>
                    <span
                      className={`text-h5 rounded-full px-1.5 ${
                        selected
                          ? "bg-white/20"
                          : count === 0
                            ? "bg-amber-100 text-amber-700"
                            : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Initials editor for the active signer — apparaissent juste
              avant la date sur le bloc signé. */}
          {activeSigner && onSignerInitialsChange && (
            <div className="flex flex-col gap-1">
              <label
                htmlFor="active-signer-initials"
                className="text-h4 font-medium text-neutral-600"
              >
                Initiales de {activeSigner.name}
              </label>
              <input
                id="active-signer-initials"
                type="text"
                value={activeSigner.initials || ""}
                onChange={(e) => {
                  const cleaned = e.target.value
                    .toUpperCase()
                    .replace(/[^A-ZÀ-ÖØ-Þ.\-\s]/g, "")
                    .slice(0, 6);
                  onSignerInitialsChange(activeSigner.id, cleaned);
                }}
                maxLength={6}
                className="w-32 px-2 py-1.5 rounded-md border border-neutral-300 text-h3 font-semibold tracking-wider text-neutral-700 bg-neutral-50 outline-none focus:border-neutral-500"
                placeholder="JD"
                autoComplete="off"
              />
              <div className="text-h5 text-neutral-400">
                Peintes juste avant la date sur le bloc signé.
              </div>
            </div>
          )}

          {/* Single draggable block */}
          {activeSigner && (
            <SidebarDraggableField
              field={`Bon pour accord — ${activeSigner.name}`}
              type="Signature"
              signerId={activeSigner.id}
            />
          )}

          {/* Fields summary per signer */}
          <div className="flex flex-col gap-2 mt-1">
            <div className="text-h4 font-medium text-neutral-600">
              Champs placés
            </div>
            {fields.length === 0 ? (
              <div className="text-h5 text-neutral-400 italic">
                Aucun champ placé. Glissez la zone « Bon pour accord » sur le document.
              </div>
            ) : (
              <div className="flex flex-col gap-1">
                {fields.map((f) => {
                  const signer = signers!.find((s) => s.id === f.signer_id);
                  return (
                    <div
                      key={f.id}
                      className="flex flex-row items-center justify-between gap-2 px-2 py-1 rounded-md bg-neutral-50 hover:bg-neutral-100"
                    >
                      <div className="flex flex-row items-center gap-2 min-w-0">
                        <span
                          className={`w-2 h-2 rounded-full shrink-0 ${
                            signer?.color || "bg-neutral-300"
                          }`}
                        />
                        <span className="text-h5 text-neutral-700 truncate">
                          {signer?.name || "—"} · page {f.page}
                        </span>
                      </div>
                      {onFieldDelete && (
                        <button
                          onClick={() => onFieldDelete(f.id)}
                          className="text-neutral-400 hover:text-red-500 shrink-0"
                          title="Retirer"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-auto flex flex-col gap-2 pt-3">
            <button
              className="w-full px-4 py-2.5 bg-neutral-100 text-neutral-800 rounded-lg hover:bg-neutral-200 text-h3"
              onClick={onClose}
            >
              Annuler
            </button>
            <button
              className="w-full px-4 py-2.5 bg-neutral-900 text-white rounded-lg hover:bg-neutral-800 text-h3 disabled:bg-neutral-300 disabled:cursor-not-allowed"
              onClick={onSign}
              disabled={loadingData}
            >
              {loadingData ? "Traitement..." : signButtonLabel}
            </button>
          </div>
        </div>
      )}

      {editorMode === "sign" && !inlineMode && (
        <div className="flex flex-col gap-3 h-full">
          <div className="text-lg font-semibold">Champs</div>
          <div className="text-sm text-gray-600 mb-2">
            Glissez-déposez les champs sur le PDF pour les positionner. Vous
            pouvez déplacer, redimensionner et modifier les champs texte.
          </div>
          {defaultFieldTypes.map((fieldType) => (
            <SidebarDraggableField key={fieldType} field={fieldType} type={fieldType} />
          ))}
          {customTextFields.map((fieldName, index) => (
            <div
              key={index}
              className="flex flex-row justify-between items-center w-full gap-2"
            >
              <SidebarDraggableField field={fieldName} type="text" />
              {handleRemoveTextField && (
                <div
                  className="flex items-center cursor-pointer border border-gray-300 rounded-full p-1 hover:bg-red-400 hover:border-red-400"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTextField(index);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="gray"
                    width="18"
                    height="18"
                  >
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </div>
              )}
            </div>
          ))}
          {setIsModalOpen && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 flex items-center justify-center gap-2 text-base"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="gray"
                width="18"
                height="18"
              >
                <path d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z" />
              </svg>
              <span>Nouveau champ</span>
            </button>
          )}
          <div className="flex flex-grow w-full gap-3 flex-col justify-end">
            <button
              className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
              onClick={() => handleEditorMode("choice")}
            >
              Annuler
            </button>
            <button
              className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed text-base"
              onClick={onSign}
              disabled={loadingData}
            >
              {loadingData ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Traitement...
                </>
              ) : (
                signButtonLabel
              )}
            </button>
          </div>
        </div>
      )}

      {editorMode === "edit" && (
        <div className="flex flex-col gap-4 h-full">
          <button
            className="w-full px-6 py-3 bg-gray-200 text-black rounded-lg hover:bg-gray-300 text-base"
            onClick={() => {
              onlyEdit ? onClose() : handleEditorMode("choice");
            }}
          >
            Annuler
          </button>
          <div className="text-sm text-gray-600">
            Déposez un champ texte sur le PDF pour le modifier. Une fois déposé,
            vous pouvez le déplacer, le redimensionner et modifier son contenu.
          </div>
          <div className="w-full">
            <SidebarDraggableField field="Champ Texte" type="Text" />
          </div>
          <div className="flex flex-col gap-3">
            <button
              className="w-full px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:bg-gray-300 disabled:cursor-not-allowed text-base"
              onClick={onEdit}
              disabled={fields.length === 0 || loadingData}
            >
              {loadingData ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Enregistrement...
                </div>
              ) : (
                editButtonLabel
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignatureSidebar;
